import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import TableHeader from "./TableHeader";
import TableRow    from "./TableRow";
import SkeletonRow from "./SkeletonRow";

const GROUP_BY_KEY = {
  stage:    row => row.stage_name    || "No Stage",
  status:   row => (row.status || "unknown").replace(/_/g, " "),
  assignee: row => row.assignee_name || "Unassigned",
  none:     null,
};

function groupRows(rows, groupBy) {
  const keyFn = GROUP_BY_KEY[groupBy];
  if (!keyFn) return [{ label: null, rows }];
  const map = new Map();
  rows.forEach(row => {
    const k = keyFn(row);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(row);
  });
  return Array.from(map.entries()).map(([label, r]) => ({ label, rows: r }));
}

export default function ListView({
  rows, loading, error, visibleCols,
  sortBy, sortDir, onSort, groupBy,
  selectedId, onRowClick,
  selectedIds, onCheckboxChange, onSelectAll,
}) {
  const scrollRef = useRef(null);
  const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.request_id));

  const groups = useMemo(() => groupRows(rows, groupBy || "none"), [rows, groupBy]);

  const colSpan = visibleCols.length + 1; // +1 for checkbox column

  if (loading && rows.length === 0) {
    return (
      <div className="t2t-list-scroll" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        <table className="table mb-0 align-middle" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHeader visibleCols={visibleCols} sortBy={sortBy} sortDir={sortDir} onSort={onSort} allSelected={false} onSelectAll={() => {}} />
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonRow key={i} cols={visibleCols} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3">
        <i className="bi bi-exclamation-triangle me-2" />{error}
      </div>
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
        <i className="bi bi-inbox display-4 mb-3" />
        <p className="fw-semibold mb-1">No requests found</p>
        <p className="small">Try different filters or search terms.</p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="t2t-list-scroll" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
      <table className="table table-hover mb-0 align-middle" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <TableHeader
          visibleCols={visibleCols}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
          allSelected={allSelected}
          onSelectAll={onSelectAll}
        />
        <tbody>
          {groups.map((group, gi) => (
            <React.Fragment key={group.label ?? `g${gi}`}>
              {/* Group header row */}
              {group.label !== null && (
                <tr>
                  <td
                    colSpan={colSpan}
                    className="bg-light fw-semibold"
                    style={{ fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase", padding: "6px 12px", color: "#6b7280", borderTop: gi > 0 ? "2px solid #e5e7eb" : undefined }}
                  >
                    {group.label}
                    <span className="ms-2 badge bg-secondary" style={{ fontSize: 10 }}>{group.rows.length}</span>
                  </td>
                </tr>
              )}
              {group.rows.map((row) => (
                <TableRow
                  key={row.request_id}
                  row={row}
                  visibleCols={visibleCols}
                  onClick={onRowClick}
                  isSelected={selectedId === row.request_id || selectedIds.has(row.request_id)}
                  onCheckboxChange={onCheckboxChange}
                />
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {loading && rows.length > 0 && (
        <div className="text-center py-2 text-muted small">
          <span className="spinner-border spinner-border-sm me-1" /> Refreshing…
        </div>
      )}

      <div className="text-center py-2 text-muted small border-top">
        {rows.length} request{rows.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

ListView.propTypes = {
  rows:            PropTypes.arrayOf(PropTypes.object).isRequired,
  loading:         PropTypes.bool,
  error:           PropTypes.string,
  visibleCols:     PropTypes.arrayOf(PropTypes.string).isRequired,
  sortBy:          PropTypes.string,
  sortDir:         PropTypes.string,
  onSort:          PropTypes.func.isRequired,
  groupBy:         PropTypes.string,
  selectedId:      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRowClick:      PropTypes.func.isRequired,
  selectedIds:     PropTypes.instanceOf(Set).isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  onSelectAll:     PropTypes.func.isRequired,
};
